import React, { ReactElement } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../enviroment";
import {
  isAnyCartPriceCheckUpdated,
  useCartPriceCheckVersion,
} from "../../../hooks/useCartPriceCheck";

// "Price updated" (flag REACT_APP_CART_PRICE_CHECK_ENABLED): the discreet
// note on a cart line whose price the cart price check REALLY changed -
// the API's worker replaced the serial's price rows and the ONE re-read
// that followed landed different figures in the line (useCartSync's diff,
// markCartPriceChanged). Renders NOTHING unless the flag is on AND one of
// `logoIds` (a logo line = its logo; a design line = its placed logos)
// carries the mark - the flag-off DOM is byte-identical to today. Same
// family as the "Prices on their way" status line it sits beside: the 10 px
// semibold status text with the 0.25rem dot in front, in the success tone
// (a price that moved is information, never an error). Plain text, no
// role of its own: in the price block of a line's popup it is there when
// the popup opens; on the cart strip it mounts INTO the permanent live
// region CartPriceUpdatedStatus, which announces it. Module state, not
// persisted: the note goes with a remove of the line, a clear of the cart,
// a NEW check of the logo (a re-add after the cooldown) and a reload.
// Never a toast, never blocking.
export function CartPriceUpdatedNote({
  logoIds,
  className,
}: {
  logoIds: readonly number[];
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  useCartPriceCheckVersion();
  if (!enviroment.cartPriceCheckEnabled) return null;
  if (!isAnyCartPriceCheckUpdated(logoIds)) return null;
  return (
    <span
      data-testid="cart-price-updated-note"
      className={`inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-status-success ${
        className ?? ""
      }`}
    >
      <span
        aria-hidden="true"
        className="h-[0.25rem] w-[0.25rem] shrink-0 rounded-full bg-status-success"
      />
      {t("orderCreate.priceUpdated")}
    </span>
  );
}

// The cart strip's live region (OrderLinesList): a PERMANENT polite status
// container the strip's note mounts INTO, so a screen reader announces
// "Price updated" when it appears - a live region must exist in the DOM
// before its content changes; a region inserted together with its text is
// skipped by most reader/browser pairs (NVDA, VoiceOver). Empty - no text,
// no height - until a line carries the mark; the note inside is the
// visible text itself, so nothing is read twice. Renders NOTHING with the
// flag off: the flag-off DOM stays byte-identical.
export function CartPriceUpdatedStatus({
  logoIds,
}: {
  logoIds: readonly number[];
}): ReactElement | null {
  if (!enviroment.cartPriceCheckEnabled) return null;
  return (
    <div
      data-testid="cart-price-updated-status"
      role="status"
      aria-live="polite"
    >
      <CartPriceUpdatedNote logoIds={logoIds} />
    </div>
  );
}

// The same signal on the cart's thumbnail strip, where there is no room
// for text: a small dot in the block's BOTTOM right corner - the top right
// belongs to Closeable's remove cross (OrderLinesList wraps every line in
// it), which must never share a corner with a hover target - with the note
// as its tooltip and as its accessible name (role img, so the label is
// announced). Touch users get the text from the strip's own note below
// the thumbnails (OrderLinesList). Same gate, same state, same silence
// with the flag off.
export function CartPriceUpdatedMark({
  logoIds,
}: {
  logoIds: readonly number[];
}): ReactElement | null {
  const { t } = useTranslation();
  useCartPriceCheckVersion();
  if (!enviroment.cartPriceCheckEnabled) return null;
  if (!isAnyCartPriceCheckUpdated(logoIds)) return null;
  return (
    <Tooltip title={t("orderCreate.priceUpdated")} placement="top">
      <span
        data-testid="cart-price-updated-mark"
        role="img"
        aria-label={t("orderCreate.priceUpdated")}
        className="absolute bottom-1 right-1 h-[0.375rem] w-[0.375rem] rounded-full bg-status-success"
      />
    </Tooltip>
  );
}
